import {
	type CSSProperties,
	FC,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type {
	ExerciseActualSet,
	ExerciseParams,
} from '@pages/TrainingConstructor/types'
import { ChevronDown, Clock3, GripVertical, X } from 'lucide-react'
import { Image } from '@pages/TrainingConstructor/ui/exerciseCard/image.ui'
import { NumberStepper } from './numberStepper'
import {
	type WheelOption,
	WheelPicker,
} from '@shared/ui/ProfileInfoForm'

interface SortableExerciseItemProps {
	id: number | string
	params: ExerciseParams
	onParamsChange: (params: ExerciseParams) => void
	onDelete: (id: number) => void
	isExpanded: boolean
	onExpandedChange: (isExpanded: boolean) => void
	isReadonly?: boolean
	readonlySummaryLabel?: string
}

const timeOptions: WheelOption[] = Array.from({ length: 121 }, (_, index) => ({
	value: index,
	label: `${index}`,
}))

const clampTime = (value: number | undefined): number => {
	if (!Number.isFinite(value)) {
		return 0
	}

	return Math.max(0, Math.min(120, Number(value)))
}

const secondsLabel = (value: number | undefined): string => `${value ?? 0} сек`

const formatReadonlyValue = (value: number | undefined, fallback: number): string =>
	String(value ?? fallback)

const getNumericValue = (value: number | null | undefined, fallback = 0): number => {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value
	}

	return fallback
}

const getActualSets = (params: ExerciseParams): ExerciseActualSet[] => {
	if (params.actualSets?.length) {
		return params.actualSets
	}

	const setsAmount = params.type === 'strength' ? Math.max(params.sets ?? 1, 1) : 1

	return Array.from({ length: setsAmount }, () => ({
		reps: params.type === 'strength' ? params.reps : undefined,
		duration: params.duration,
		rest_time: params.rest_time,
	}))
}

const getSetDuration = (set: ExerciseActualSet, params: ExerciseParams): number =>
	getNumericValue(set.duration ?? set.work_time_sec, params.duration ?? 0)

const getSetRestTime = (set: ExerciseActualSet, params: ExerciseParams): number =>
	getNumericValue(set.rest_time ?? set.rest_time_sec, params.rest_time ?? 0)

const getReadonlySummary = (
	params: ExerciseParams,
	actualSets: ExerciseActualSet[],
): {
	leftLabel: string
	leftValue: string
	rightLabel: string
	rightValue: string
} => {
	if (params.type === 'strength') {
		const completedSets = Math.max(actualSets.length, params.sets ?? 0)
		const completedReps = actualSets.reduce(
			(total, set) => total + getNumericValue(set.reps, params.reps ?? 0),
			0,
		)

		return {
			leftLabel: 'подходов',
			leftValue: formatReadonlyValue(completedSets, 0),
			rightLabel: 'повторов',
			rightValue: formatReadonlyValue(completedReps, params.reps ?? 0),
		}
	}

	const totalDuration = actualSets.reduce(
		(total, set) => total + getSetDuration(set, params),
		0,
	)
	const totalRestTime = actualSets.reduce(
		(total, set) => total + getSetRestTime(set, params),
		0,
	)

	return {
		leftLabel: 'сек работы',
		leftValue: formatReadonlyValue(totalDuration, params.duration ?? 0),
		rightLabel: 'сек отдыха',
		rightValue: formatReadonlyValue(totalRestTime, params.rest_time ?? 0),
	}
}

const ReadonlyActualResults: FC<{
	actualSets: ExerciseActualSet[]
	params: ExerciseParams
}> = ({ actualSets, params }) => (
	<div className="training-modal-actual-results">
		{actualSets.map((set, index) => {
			const leftValue =
				params.type === 'strength'
					? getNumericValue(set.reps, params.reps ?? 0)
					: getSetDuration(set, params)
			const leftLabel =
				params.type === 'strength' ? 'повторов' : 'время выполнения'
			const rightValue = getSetRestTime(set, params)
			const rightLabel = 'время отдыха'

			return (
				<div className="training-modal-actual-row" key={index}>
					<span className="training-modal-actual-row-label">
						Подход {index + 1}
					</span>
					<span className="training-modal-actual-cell">
						<strong>{leftValue}</strong>
						<small>{leftLabel}</small>
					</span>
					<b aria-hidden="true">×</b>
					<span className="training-modal-actual-cell">
						<strong>{rightValue}</strong>
						<small>{rightLabel}</small>
					</span>
					{params.type === 'strength' && (
						<span className="training-modal-actual-row-note">
							Время выполнения: <strong>{getSetDuration(set, params)}</strong> сек
						</span>
					)}
				</div>
			)
		})}
	</div>
)

const TimeField: FC<{
	title: string
	value?: number
	onChange: (value: number) => void
	onOpenChange: (isOpen: boolean) => void
}> = ({ title, value, onChange, onOpenChange }) => {
	const [isPickerOpen, setIsPickerOpen] = useState(false)
	const [pickerStyle, setPickerStyle] = useState<CSSProperties | null>(null)
	const triggerRef = useRef<HTMLButtonElement | null>(null)
	const safeValue = clampTime(value)
	const togglePicker = () => {
		setIsPickerOpen((current) => {
			const next = !current
			onOpenChange(next)
			return next
		})
	}

	useLayoutEffect(() => {
		if (!isPickerOpen) return

		const updatePickerPosition = () => {
			const trigger = triggerRef.current
			if (!trigger) return

			const rect = trigger.getBoundingClientRect()
			const width = Math.min(rect.width, 176)
			const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))

			setPickerStyle({
				position: 'fixed',
				top: rect.bottom + 8,
				left,
				width,
				zIndex: 10010,
			})
		}

		updatePickerPosition()
		window.addEventListener('resize', updatePickerPosition)
		window.addEventListener('scroll', updatePickerPosition, true)

		return () => {
			window.removeEventListener('resize', updatePickerPosition)
			window.removeEventListener('scroll', updatePickerPosition, true)
		}
	}, [isPickerOpen])

	useEffect(() => {
		return () => onOpenChange(false)
	}, [onOpenChange])

	return (
		<div className={`training-modal-time-field ${isPickerOpen ? 'is-open' : ''}`}>
			<button
				ref={triggerRef}
				type="button"
				className="training-modal-time-trigger"
				onClick={togglePicker}
				aria-expanded={isPickerOpen}
			>
				<span>
					<Clock3 aria-hidden="true" />
					{title}
				</span>
				<strong>{secondsLabel(safeValue)}</strong>
			</button>
			{isPickerOpen &&
				pickerStyle &&
				createPortal(
					<div className="training-modal-time-picker" style={pickerStyle}>
						<WheelPicker
							ariaLabel={title}
							options={timeOptions}
							value={safeValue}
							onChange={onChange}
						/>
					</div>,
					document.body,
				)}
		</div>
	)
}

export const SortableExerciseItem: FC<SortableExerciseItemProps> = ({
	id,
	params,
	onParamsChange,
	onDelete,
	isExpanded,
	onExpandedChange,
	isReadonly = false,
	readonlySummaryLabel,
}) => {
	const [hasOpenPicker, setHasOpenPicker] = useState(false)
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.72 : 1,
		zIndex: isDragging ? 40 : hasOpenPicker ? 30 : isExpanded ? 10 : 1,
	}

	const secondaryMuscles = useMemo(
		() => params.secondaryMuscles?.slice(0, 3).join(' • ') ?? '',
		[params.secondaryMuscles],
	)
	const actualSets = useMemo(() => getActualSets(params), [params])
	const readonlySummary = useMemo(
		() => getReadonlySummary(params, actualSets),
		[actualSets, params],
	)
	const toggleExpanded = () => {
		onExpandedChange(!isExpanded)
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`training-modal-exercise ${isExpanded ? 'is-expanded' : ''} ${isDragging ? 'is-dragging' : ''} ${isReadonly ? 'is-readonly' : ''}`}
		>
			<div className="training-modal-exercise-row">
				{!isReadonly && (
					<button
						type="button"
						className="training-modal-drag"
						{...attributes}
						{...listeners}
						style={{ touchAction: 'none' }}
						aria-label="Изменить порядок упражнения"
					>
						<GripVertical aria-hidden="true" />
					</button>
				)}
				{!isReadonly && (
					<span className="training-modal-exercise-order">{params.order}</span>
				)}
				<button
					type="button"
					className="training-modal-exercise-image-trigger"
					onClick={toggleExpanded}
					aria-label={isExpanded ? 'Свернуть упражнение' : 'Раскрыть упражнение'}
					aria-expanded={isExpanded}
				>
					<Image src={params.imageUrl} />
				</button>
				<button
					type="button"
					className="training-modal-exercise-main"
					onClick={toggleExpanded}
					aria-expanded={isExpanded}
				>
					<strong>{params.exerciseName}</strong>
					{params.primaryMuscle && <span>{params.primaryMuscle}</span>}
					{secondaryMuscles && <small>{secondaryMuscles}</small>}
				</button>
				<div className="training-modal-exercise-actions">
					{isReadonly && (
						<span className="training-modal-exercise-order">{params.order}</span>
					)}
					<button
						type="button"
						className="training-modal-icon-button"
						onClick={toggleExpanded}
						aria-label={isExpanded ? 'Свернуть настройки' : 'Раскрыть настройки'}
						aria-expanded={isExpanded}
					>
						<ChevronDown aria-hidden="true" />
					</button>
					{!isReadonly && (
						<button
							type="button"
							className="training-modal-icon-button"
							onClick={() => onDelete(params.exercise_id)}
							aria-label="Удалить упражнение"
						>
							<X aria-hidden="true" />
						</button>
					)}
				</div>
			</div>
			{isReadonly && readonlySummaryLabel && (
				<div className="training-modal-readonly-summary-label">
					{readonlySummaryLabel}
				</div>
			)}
			{isReadonly && (
				<div className="training-modal-readonly-settings">
					<span>
						<strong>{readonlySummary.leftValue}</strong>
						<small>{readonlySummary.leftLabel}</small>
					</span>
					<b aria-hidden="true">×</b>
					<span>
						<strong>{readonlySummary.rightValue}</strong>
						<small>{readonlySummary.rightLabel}</small>
					</span>
				</div>
			)}
			{isExpanded && isReadonly && (
				<ReadonlyActualResults actualSets={actualSets} params={params} />
			)}
			{isExpanded && !isReadonly && (
				<div className="training-modal-exercise-settings">
					<div className="training-modal-settings-grid">
						{params.type === 'strength' && (
							<>
								<NumberStepper
									title="Подходы"
									value={params.sets ?? 1}
									onChange={(value) => onParamsChange({ ...params, sets: value })}
									min={1}
									max={20}
								/>
								<NumberStepper
									title="Повторения"
									value={params.reps ?? 1}
									onChange={(value) => onParamsChange({ ...params, reps: value })}
									min={1}
									max={100}
								/>
							</>
						)}
						<TimeField
							title="Время выполнения"
							value={params.duration ?? 60}
							onChange={(value) => onParamsChange({ ...params, duration: value })}
							onOpenChange={setHasOpenPicker}
						/>
						<TimeField
							title="Отдых между подходами"
							value={params.rest_time ?? 90}
							onChange={(value) => onParamsChange({ ...params, rest_time: value })}
							onOpenChange={setHasOpenPicker}
						/>
					</div>
				</div>
			)}
		</div>
	)
}
